import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    FormControlLabel,
    Switch,
    Typography,
    Grid,
    Paper,
    InputAdornment,
    IconButton,
    Divider,
    Avatar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import EuroIcon from '@mui/icons-material/Euro';
import DiscountIcon from '@mui/icons-material/Discount';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SchoolIcon from '@mui/icons-material/School';
import api from '../../services/api';
import { Parcours } from '../../types/course';

interface Offer {
    _id?: string;
    title: string;
    description: string;
    price: number;
    discountPrice?: number;
    monthlyPrice?: number;
    parcours: string[];
    isActive: boolean;
}

interface OfferModalProps {
    open: boolean;
    onClose: () => void;
    onSave: (offer: Offer) => void;
    initialData?: Offer;
    isEditing: boolean;
}

const OfferModal: React.FC<OfferModalProps> = ({ open, onClose, onSave, initialData, isEditing }) => {
    const [formData, setFormData] = useState<Offer>({
        title: '',
        description: '',
        price: 0,
        discountPrice: 0,
        monthlyPrice: 0,
        parcours: [],
        isActive: true
    });
    const [availableParcours, setAvailableParcours] = useState<Parcours[]>([]);

    useEffect(() => {
        if (open) {
            fetchParcours();
            if (initialData) {
                setFormData({
                    ...initialData,
                    parcours: initialData.parcours.map((p: any) => typeof p === 'object' ? p._id : p)
                });
            } else {
                setFormData({
                    title: '',
                    description: '',
                    price: 0,
                    discountPrice: 0,
                    monthlyPrice: 0,
                    parcours: [],
                    isActive: true
                });
            }
        }
    }, [open, initialData]);

    const fetchParcours = async () => {
        try {
            const res = await api.get('/api/course/parcours');
            setAvailableParcours(res.data);
        } catch (err) {
            console.error("Error fetching parcours", err);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleParcoursChange = (event: any) => {
        const { target: { value } } = event;
        setFormData({
            ...formData,
            parcours: typeof value === 'string' ? value.split(',') : value,
        });
    };

    const handleSubmit = () => {
        onSave(formData);
        onClose();
    };

    // Calculate discount percentage
    const discountPercentage = formData.price && formData.discountPrice && formData.discountPrice < formData.price
        ? Math.round((1 - formData.discountPrice / formData.price) * 100)
        : 0;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    overflow: 'hidden',
                }
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                        <LocalOfferIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}>
                            {isEditing ? 'Modifier l\'Offre' : 'Nouvelle Offre'}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {isEditing ? 'Modifiez les détails de votre offre' : 'Créez une nouvelle offre commerciale'}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 4 }}>
                <Grid container spacing={3}>
                    {/* Left Column - Basic Info */}
                    <Grid size={{ xs: 12, md: 7 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                bgcolor: '#f8f9fa',
                                height: '100%',
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#667eea' }}>
                                Informations générales
                            </Typography>

                            <TextField
                                fullWidth
                                name="title"
                                label="Titre de l'offre"
                                placeholder="Ex: Formation Complète Développeur Web"
                                value={formData.title}
                                onChange={handleChange}
                                sx={{
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        bgcolor: 'white',
                                    }
                                }}
                            />

                            <TextField
                                fullWidth
                                name="description"
                                label="Description"
                                placeholder="Décrivez votre offre..."
                                multiline
                                rows={4}
                                value={formData.description}
                                onChange={handleChange}
                                sx={{
                                    mb: 3,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        bgcolor: 'white',
                                    }
                                }}
                            />

                            <Divider sx={{ mb: 3 }} />

                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#667eea', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SchoolIcon fontSize="small" />
                                Parcours inclus
                            </Typography>

                            <FormControl fullWidth>
                                <InputLabel>Sélectionnez les parcours</InputLabel>
                                <Select
                                    multiple
                                    value={formData.parcours}
                                    onChange={handleParcoursChange}
                                    label="Sélectionnez les parcours"
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => {
                                                const p = availableParcours.find(ap => ap._id === value);
                                                return (
                                                    <Chip
                                                        key={value}
                                                        label={p?.title}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: '#667eea',
                                                            color: 'white',
                                                            fontWeight: 500,
                                                        }}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    )}
                                    sx={{
                                        borderRadius: 2,
                                        bgcolor: 'white',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderRadius: 2,
                                        }
                                    }}
                                >
                                    {availableParcours.map((parcours) => (
                                        <MenuItem key={parcours._id} value={parcours._id}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <SchoolIcon fontSize="small" color="primary" />
                                                {parcours.title}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Paper>
                    </Grid>

                    {/* Right Column - Pricing */}
                    <Grid size={{ xs: 12, md: 5 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                border: '2px solid #667eea',
                                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#667eea' }}>
                                Configuration des prix
                            </Typography>

                            <TextField
                                fullWidth
                                name="price"
                                label="Prix de base"
                                type="number"
                                value={formData.price}
                                onChange={handleChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <EuroIcon color="primary" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        bgcolor: 'white',
                                    }
                                }}
                            />

                            <TextField
                                fullWidth
                                name="discountPrice"
                                label="Prix promotionnel"
                                type="number"
                                value={formData.discountPrice}
                                onChange={handleChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <DiscountIcon color="secondary" />
                                        </InputAdornment>
                                    ),
                                }}
                                helperText={discountPercentage > 0 ? `Réduction de ${discountPercentage}%` : 'Laissez vide ou 0 pour pas de promotion'}
                                sx={{
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        bgcolor: 'white',
                                    }
                                }}
                            />

                            <TextField
                                fullWidth
                                name="monthlyPrice"
                                label="Prix mensuel (optionnel)"
                                type="number"
                                value={formData.monthlyPrice}
                                onChange={handleChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <CalendarMonthIcon color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">/mois</InputAdornment>
                                    ),
                                }}
                                sx={{
                                    mb: 3,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        bgcolor: 'white',
                                    }
                                }}
                            />

                            <Divider sx={{ mb: 2 }} />

                            {/* Preview Card */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white',
                                    textAlign: 'center',
                                }}
                            >
                                <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mb: 1 }}>
                                    Aperçu du prix
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1 }}>
                                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                        {formData.discountPrice || formData.price || 0}€
                                    </Typography>
                                    {(formData.discountPrice || 0) > 0 && formData.price > (formData.discountPrice || 0) && (
                                        <Typography variant="body1" sx={{ textDecoration: 'line-through', opacity: 0.7 }}>
                                            {formData.price}€
                                        </Typography>
                                    )}
                                </Box>
                                {(formData.monthlyPrice || 0) > 0 && (
                                    <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                                        ou {formData.monthlyPrice}€/mois
                                    </Typography>
                                )}
                            </Paper>

                            <Divider sx={{ my: 2 }} />

                            {/* Active Switch */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 2,
                                    bgcolor: 'white',
                                    borderRadius: 2,
                                }}
                            >
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        Offre active
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        L'offre sera visible publiquement
                                    </Typography>
                                </Box>
                                <Switch
                                    checked={formData.isActive}
                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                    color="primary"
                                />
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0, gap: 2 }}>
                <Button
                    onClick={onClose}
                    sx={{
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                    }}
                >
                    Annuler
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    sx={{
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
                        '&:hover': {
                            boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                        }
                    }}
                >
                    {isEditing ? 'Mettre à jour' : 'Créer l\'offre'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OfferModal;
